<template>
    <div class="card mt-3 bg-light">
        <div class="card-header">
            <h6 class="d-inline">Query {{ query.queryId }}</h6>
            <button
                class="btn btn-sm btn-light float-right d-inline py-0"
                @click="removeQuery">
                &times; Remove
            </button>
        </div>
        <div class="card-body">
            <div class="row">
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Query Name</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': queryNameError }"
                            v-model="query.queryName"
                            @change="validateQueryName" />
                        <div class="invalid-feedback">Max. 8 alphanumeric characters only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Slave ID</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': slaveIdError }"
                            v-model="query.slaveId"
                            @change="validateSlaveId" />
                        <div class="invalid-feedback">Numbers ranging from 1 to 247 only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Function Code</label>
                    </div>
                    <div class="col-md-8">
                        <select
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': functionCodeError }"
                            v-model="query.functionCode"
                            @change="validateStartAddress">
                            <option value="1">1 - Read Coil</option>
                            <option value="2">2 - Read Discrete Input</option>
                            <option value="3">3 - Read Holding Registers</option>
                            <option value="4">4 - Read Input Registers</option>
                            <option value="5">5 - Write Single Coil</option>
                            <option value="6">6 - Write Single Holding Register</option>
                        </select>
                        <div class="invalid-feedback">Required field. Please select one</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Start Address</label>
                    </div>
                    <div class="col-md-8">
                        <div class="input-group">
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                :class="{ 'is-invalid': startAddressError }"
                                v-model="query.startAddress"
                                @change="validateStartAddress" />
                            <div class="invalid-feedback">Numbers ranging from 0 to 65535 only</div>
                        </div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Number of Registers</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': numberOfRegistersError }"
                            v-model="query.numberOfRegisters"
                            @change="validateNumberOfRegisters" />
                        <div class="invalid-feedback">Numbers ranging from 1 to 65535 only</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">IP Address (TCP mode)</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': ipAddressError }"
                            v-model="query.ipAddress"
                            @change="validateIpAddress" />
                        <div class="invalid-feedback">Please follow IP address format</div>
                    </div>
                </div>
                <div class="form-group row col-6">
                    <div class="col-4">
                        <label class="col-form-label col-form-label-sm">Port# (TCP mode)</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': portError }"
                            v-model="query.port"
                            @change="validatePort" />
                        <div class="invalid-feedback">Numbers ranging from 1 to 65535 only</div>
                    </div>
                </div>
            </div>
            <tag
                v-if="query.tagArr.length > 0"
                v-for="tag in query.tagArr"
                :key="tag.tagId"
                :query="query"
                :tag="tag"
                :loggingMethod="loggingMethod"
                @tagRemoved="removeTag($event)"></tag>
            <button
                class="btn btn-outline-info btn-sm mt-3"
                @click="addTag">+ Add Tag</button>
        </div>
    </div>
</template>

<script>
import Tag from './Tag.vue'

export default {
    components: {
        'tag': Tag
    },
    data() {
        return {
            queryNameError: false,
            slaveIdError: false,
            functionCodeError: false,
            startAddressError: false,
            numberOfRegistersError: false,
            ipAddressError: false,
            portError: false
        }
    },
    props: ['modbusConfig', 'query', 'loggingMethod'],
    mounted() {
        this.query.queryName = `Query${this.query.queryId}`
    },
    methods: {
        addTag() {
            this.$emit('tagAdded', this.query.queryId)
        },
        validateQueryName() {
            if(/^[A-Za-z0-9]{1,8}$/.test(this.query.queryName)) {
                this.queryNameError = false
            } else {
                this.queryNameError = true
            }
        },
        validateSlaveId() {
            if(/^[0-9]+$/.test(this.query.slaveId) && (parseInt(this.query.slaveId) > 0 && parseInt(this.query.slaveId) < 248)) {
                this.slaveIdError = false
            } else {
                this.slaveIdError = true
            }
        },
        validateStartAddress() {
            if(this.query.functionCode === '') {
                this.functionCodeError = true
            } else {
                this.functionCodeError = false
            }
            if(/^[0-9]+$/.test(this.query.startAddress) && (parseInt(this.query.startAddress) >= 0 && parseInt(this.query.startAddress) < 65536)) {
                this.startAddressError = false
            } else {
                this.startAddressError = true
            }
        },
        validateNumberOfRegisters() {
            if(/^[0-9]+$/.test(this.query.numberOfRegisters) && (parseInt(this.query.numberOfRegisters) > 0 && parseInt(this.query.numberOfRegisters) < 65536)) {
                this.numberOfRegistersError = false
            } else {
                this.numberOfRegistersError = true
            }
        },
        validateIpAddress() {
            if(/^\d{1,3}.\d{1,3}.\d{1,3}.\d{1,3}$/.test(this.query.ipAddress) || this.query.ipAddress === 'NULL') {
                this.ipAddressError = false
            } else {
                this.ipAddressError = true
            }
        },
        validatePort() {
            if((/^[0-9]+$/.test(this.query.port) && (parseInt(this.query.port) > 0 && parseInt(this.query.port) < 65536)) || this.query.port === 'NULL') {
                this.portError = false
            } else {
                this.portError = true
            }
        },
        removeQuery() {
            this.$emit('queryRemoved', { queryId: this.query.queryId })
        },
        removeTag($event) {
            this.$emit('tagRemoved', {
                tagId: $event.tagId,
                queryId: this.query.queryId
            })
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/polling-group/Query.vue